import { useState, MouseEvent, useEffect } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fade from '@mui/material/Fade';
import { useNavigate } from 'react-router-dom';
import './style.css';
import { ListItemIcon, Typography } from '@mui/material';
import Icons from '../../Icon/icons';
import { User } from '../../../shared/types/user';
import { Role } from '../../../shared/types/role';
import { getMe } from '../../../features/search/service/user-service';
import { getRoleById } from '../../../features/search/service/role-service';
import { useTranslation } from '../../../shared/i18n/i18n-context';
import { ROLES } from '../../../shared/consts/roles';

const normalizeRoleValue = (value?: string | null) => value?.toLowerCase().trim() || '';

const roleMatches = (role: Role | null, allowedRoles: string[]) => {
    const currentRoleValues = [
        normalizeRoleValue(role?.name),
        normalizeRoleValue(role?.description)
    ];

    return allowedRoles.some((roleName) => currentRoleValues.includes(normalizeRoleValue(roleName)));
};

export const MenuTop = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('menu');
    const { t: adminT } = useTranslation('admin');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [adminMenuOpen, setAdminMenuOpen] = useState(false);
    const [me, setMe] = useState<User | null>(null);
    const [meRole, setMeRole] = useState<Role | null>(null);
    const open = Boolean(anchorEl);
    const isSuperAdmin = roleMatches(meRole, [ROLES.SUPER_ADMIN]);
    const canAccessAdmin = roleMatches(meRole, [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.EMPRESA]);
    const adminRootPath = isSuperAdmin ? '/admin-painel-causale' : '/admin-painel';
    const adminRootLabel = isSuperAdmin ? adminT('causalePanel') : adminT('adminPanel');
    
    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleHome = () => {
        handleClose();
        navigate('/home');
    };

    const handleRoute = () => {
        handleClose();
        navigate('/relatorio-pesquisa');
    };

    const handleRouteAdmin = () => {
        handleClose();
        navigate(adminRootPath);
    };

    const handleCompaniesDashboard = () => {
        handleClose();
        navigate('/admin-painel/companies-dashboard');
    };

    const handleAdminOption = (path: string) => {
        setAdminMenuOpen(false);
        navigate(path);
    };

    const handleProfile = () => {
        handleClose();
        navigate('/profile');
    };

    const handleLogout = () => {
        handleClose();
        localStorage.removeItem('token');
        navigate('/');
    };

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const userData = await getMe();
                setMe(userData);

                const roleData = userData.role || (userData.role_id ? await getRoleById(userData.role_id) : null);

                if (roleData) {
                    setMeRole(roleData as Role);
                }
            } catch (error) {
                console.error('❌ Erro ao buscar dados do usuário:', error);
            }
        };
        fetchUser();
    }, []);

    return (
        <>
        <div className="menu-top-container">
            <button className="menu-top-logo-button" type="button" onClick={handleHome} aria-label={t('home')}>
                <img src="/assets/logo-inteira.png" alt="Delay Discounting" className="menu-top-logo" />
            </button>

            <nav className="menu-top-desktop-nav" aria-label={t('menu')}>
                <button type="button" className="menu-top-link" onClick={handleHome}>
                    <Icons iconName="Home" addTooltip={t('home')} color="currentColor" fontSize={21} />
                    {t('home')}
                </button>
                <button type="button" className="menu-top-link" onClick={handleRoute}>
                    <Icons iconName="ContentPasteSearch" addTooltip={t('generateReport')} color="currentColor" fontSize={21} />
                    {t('reportShort')}
                </button>
                {me && canAccessAdmin && (
                    <div
                        className="menu-top-dropdown-wrapper"
                        onMouseEnter={() => setAdminMenuOpen(true)}
                        onMouseLeave={() => setAdminMenuOpen(false)}
                    >
                        <button
                            type="button"
                            className="menu-top-link menu-top-admin-trigger"
                            onClick={handleRouteAdmin}
                            onFocus={() => setAdminMenuOpen(true)}
                            aria-haspopup="menu"
                            aria-expanded={adminMenuOpen}
                        >
                            <Icons iconName="AdminPanelSettings" addTooltip={adminRootLabel} color="currentColor" fontSize={21} />
                            {t('adminShort')}
                            <Icons iconName="KeyboardArrowDown" color="currentColor" fontSize={19} />
                        </button>

                        <div className={`menu-top-admin-dropdown ${adminMenuOpen ? 'is-open' : ''}`} role="menu">
                            {isSuperAdmin ? (
                                <>
                                    <button type="button" role="menuitem" onClick={() => handleAdminOption(adminRootPath)}>
                                        <Icons iconName="AdminPanelSettings" color="currentColor" fontSize={20} />
                                        {adminRootLabel}
                                    </button>
                                    <button type="button" role="menuitem" onClick={() => handleAdminOption('/admin-painel/users')}>
                                        <Icons iconName="GroupAdd" color="currentColor" fontSize={20} />
                                        {adminT('listUsers')}
                                    </button>
                                    <button type="button" role="menuitem" onClick={() => handleAdminOption('/admin-painel/tests-management')}>
                                        <Icons iconName="Assignment" color="currentColor" fontSize={20} />
                                        {adminT('manageTests')}
                                    </button>
                                    <button type="button" role="menuitem" onClick={() => handleAdminOption('/admin-painel/companies-dashboard')}>
                                        <Icons iconName="Business" color="currentColor" fontSize={20} />
                                        {adminT('companies')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button type="button" role="menuitem" onClick={() => handleAdminOption(adminRootPath)}>
                                        <Icons iconName="AdminPanelSettings" color="currentColor" fontSize={20} />
                                        {adminRootLabel}
                                    </button>
                                    <button type="button" role="menuitem" onClick={() => handleAdminOption('/admin-painel/users')}>
                                        <Icons iconName="Person" color="currentColor" fontSize={20} />
                                        {adminT('listUsers')}
                                    </button>
                                    <button type="button" role="menuitem" onClick={() => handleAdminOption('/admin-painel/list-configs')}>
                                        <Icons iconName="Settings" color="currentColor" fontSize={20} />
                                        {adminT('listConfigs')}
                                    </button>
                                    <button type="button" role="menuitem" onClick={() => handleAdminOption('/admin-painel/add-user')}>
                                        <Icons iconName="PersonAdd" color="currentColor" fontSize={20} />
                                        {adminT('newUser')}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
                <button
                    type="button"
                    className="menu-top-link menu-top-profile"
                    onClick={handleProfile}
                    title={`${t('rolePrefix')} ${meRole?.description || t('loading')}`}
                >
                    <Icons iconName="Person" addTooltip={t('profile')} color="currentColor" fontSize={21} />
                    {t('profile')}
                </button>
                <button type="button" className="menu-top-logout" onClick={handleLogout}>
                    <Icons iconName="Logout" addTooltip={t('logout')} color="currentColor" fontSize={21} />
                    {t('logoutShort')}
                </button>
            </nav>

            <div className="menu-top-mobile-actions">
                <button type="button" className="menu-top-role" onClick={handleProfile}>
                    {t('rolePrefix')} {meRole?.description || t('loading')}
                </button>
                <Button
                    id="fade-button"
                    aria-controls={open ? 'fade-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    className="menu-top-button"
                >
                    <Icons iconName="MenuRounded" addTooltip={t('menu')} color="#3a8d8e" fontSize={27} />
                </Button>
            </div>
            <Menu
                id="fade-menu"
                MenuListProps={{
                    'aria-labelledby': 'fade-button',
                }}
                TransitionComponent={Fade}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuItem onClick={handleHome}>
                    <ListItemIcon>
                        <Icons iconName="Home" addTooltip={t('home')} color="#3a8d8e" fontSize={27}/>
                    </ListItemIcon>
                    <Typography color="#3a8d8e">{t('home')}</Typography>
                </MenuItem>
                <MenuItem onClick={handleRoute}>
                    <ListItemIcon>
                        <Icons iconName="ContentPasteSearch" addTooltip={t('generateReport')} color="#3a8d8e" fontSize={27}/>
                    </ListItemIcon>
                    <Typography color="#3a8d8e">{t('generateReport')}</Typography>
                </MenuItem>
                {me && canAccessAdmin && (
                    <MenuItem onClick={handleRouteAdmin}>
                        <ListItemIcon>
                            <Icons iconName="AdminPanelSettings" addTooltip={adminRootLabel} color="#3a8d8e" fontSize={27}/>
                        </ListItemIcon>
                        <Typography color="#3a8d8e">{adminRootLabel}</Typography>
                    </MenuItem>
                )}
                {me && isSuperAdmin && (
                    <MenuItem onClick={handleCompaniesDashboard}>
                        <ListItemIcon>
                            <Icons iconName="Business" addTooltip={t('companies')} color="#3a8d8e" fontSize={27}/>
                        </ListItemIcon>
                        <Typography color="#3a8d8e">{t('companies')}</Typography>
                    </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <Icons iconName="Logout" addTooltip={t('logout')} color="#3a8d8e" fontSize={27}/>
                    </ListItemIcon>
                    <Typography color="#3a8d8e">{t('logout')}</Typography>
                </MenuItem>
            </Menu>
        </div>
        <div className="menu-top-spacer" aria-hidden="true" />
        </>
    );
};
